import i18n from "i18next";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import {
  parseTitle,
  readAuthors,
  stripAuthorWrapper,
  writeAuthors,
  writeTitle,
} from "@/lib/titleAuthors";
import { testIds } from "@/testIds";
import { FieldInput } from "./FieldInput";
import { PaperPreview } from "./PaperPreview";
import { LatexBox, ModeSwitch } from "./LatexMode";
import { PreviewStrip } from "./PreviewStrip";
import { useTitleLayout, type TitleLayout } from "./useTitleLayout";
import {
  draftFromAuthors,
  draftFromNotes,
  draftSymbols,
  duplicateMessage,
  duplicateNotes,
  emptyAuthor,
  emptyField,
  fieldsToWrite,
  moveItem,
  type DAuthor,
  type DField,
} from "./titleDraft";
import styles from "./TitleEditContent.module.scss";

const T = testIds.titleEdit;

export interface TitleEditValues {
  title: string;
  author: string;
}

export interface TitleEditFocus {
  /** Index of the author to focus; omit for the Title field. */
  author?: number;
}

interface TitleEditContentProps {
  /** The stored strings, as they are today. */
  title: string;
  author: string;
  focus?: TitleEditFocus;
  /** Reports the strings a Save would write, on every change. */
  onChange: (values: TitleEditValues) => void;
  /** ⌘↵ */
  onSubmit: () => void;
  /** The Date section, unchanged from before. */
  dateSlot: ReactNode;
  /** The date as it prints, for the paper. */
  dateText: string;
  /** From this many authors, cards collapse to one line (opening one closes the others). */
  collapseFrom?: number;
  /** Override the layout the window width would pick (tests, previews). */
  layout?: TitleLayout;
}

/**
 * The body of the "Edit title page" modal. It owns the drafts and reports the
 * strings a Save would write. A string nobody edited is reported exactly as
 * it was stored: opening, and saving without a change, never rewrites it.
 */
export function TitleEditContent({ title, author, focus, onChange, onSubmit, dateSlot, dateText, collapseFrom, layout: layoutProp }: TitleEditContentProps) {
  const { layout, width: windowWidth } = useTitleLayout(layoutProp);
  const sheet = layout === "sheet";
  const collapseAt = collapseFrom ?? (sheet ? 2 : 4);
  const rootRef = useRef<HTMLDivElement>(null);
  const initialTitle = useMemo(() => parseTitle(title), [title]);

  const [titleText, setTitleText] = useState(initialTitle.text);
  const [titleNotes, setTitleNotes] = useState<DField[]>(() =>
    draftFromNotes(initialTitle.notes.map((n) => ({ text: n, chips: [] }))),
  );
  const [titleTouched, setTitleTouched] = useState(false);

  const [authors, setAuthors] = useState<DAuthor[]>(() => draftFromAuthors(readAuthors(author).authors));
  // Which side last edited the authors. "none" keeps the stored string as is.
  const [touch, setTouch] = useState<"none" | "fields" | "latex">("none");
  const [latexDraft, setLatexDraft] = useState(author);
  const [mode, setMode] = useState<"fields" | "latex">(() => (readAuthors(author).status.kind === "ok" ? "fields" : "latex"));
  // The one open card once the list is long. A new author opens; opening closes the others.
  const [openId, setOpenId] = useState<number | null>(() =>
    focus?.author === undefined ? null : (authors[focus.author]?.id ?? null),
  );
  const [focusId, setFocusId] = useState<number | string | null>(null);
  const [menuFor, setMenuFor] = useState<number | null>(null);
  const [hoverSymbol, setHoverSymbol] = useState<string | null>(null);

  const titleValue = titleTouched ? writeTitle(titleText, titleNotes) : title;
  const authorValue = touch === "none" ? author : touch === "fields" ? writeAuthors(fieldsToWrite(authors)) : latexDraft;

  const reading = useMemo(() => readAuthors(authorValue), [authorValue]);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    onChangeRef.current({ title: titleValue, author: authorValue });
  }, [titleValue, authorValue]);

  // Move focus after the render that created (or should now show) a field.
  useEffect(() => {
    if (focusId === null) return;
    rootRef.current?.querySelector<HTMLInputElement>(`[data-field-id="${focusId}"]`)?.focus();
    setFocusId(null);
  }, [focusId]);

  const editAuthors = useCallback((fn: (list: DAuthor[]) => DAuthor[]) => {
    setTouch("fields");
    setAuthors(fn);
  }, []);
  const patchAuthor = useCallback(
    (id: number, fn: (a: DAuthor) => DAuthor) => editAuthors((list) => list.map((a) => (a.id === id ? fn(a) : a))),
    [editAuthors],
  );
  const editTitleNotes = (fn: (list: DField[]) => DField[]) => {
    setTitleTouched(true);
    setTitleNotes(fn);
  };

  const fieldsBlocked = reading.status.kind !== "ok";
  const switchMode = (next: "fields" | "latex") => {
    if (next === mode || (next === "fields" && fieldsBlocked)) return;
    if (next === "latex") setLatexDraft(authorValue);
    else if (touch === "latex") setAuthors(draftFromAuthors(readAuthors(latexDraft).authors));
    setMode(next);
  };
  const editLatex = (value: string) => {
    setTouch("latex");
    setLatexDraft(stripAuthorWrapper(value) ?? value);
  };

  const symbols = draftSymbols(titleNotes, authors);
  const duplicates = duplicateNotes(authors);
  const multi = authors.length > 1;

  /** Focus the field before/after the one at `from`, in reading order. */
  const neighbour = (from: HTMLElement, step: -1 | 1): HTMLElement | undefined => {
    const all = [...(rootRef.current?.querySelectorAll<HTMLElement>("[data-field-id]") ?? [])];
    return all[all.indexOf(from) + step];
  };

  const isPlainEnter = (e: KeyboardEvent<HTMLInputElement>) =>
    e.key === "Enter" && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.nativeEvent.isComposing;

  const fieldKeys =
    (kind: "name" | "line" | "note", remove?: () => void, addAfter?: () => void) =>
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (isPlainEnter(e)) {
        e.preventDefault();
        if (kind === "line" && addAfter) addAfter();
        else neighbour(e.currentTarget, 1)?.focus();
      } else if (e.key === "Backspace" && kind !== "name" && remove && !e.currentTarget.value) {
        e.preventDefault();
        const prev = neighbour(e.currentTarget, -1);
        remove();
        prev?.focus();
      }
    };

  const addAuthor = () => {
    const a = emptyAuthor();
    editAuthors((list) => [...list, a]);
    setOpenId(a.id);
    setFocusId(a.name.id);
  };
  const removeAuthor = (id: number) =>
    editAuthors((list) => (list.length > 1 ? list.filter((a) => a.id !== id) : list));
  const moveAuthor = (index: number, by: -1 | 1, focusFieldId?: string | null) => {
    editAuthors((list) => moveItem(list, index, index + by));
    if (focusFieldId) setFocusId(focusFieldId);
  };
  const addLine = (id: number, afterLineId?: number) => {
    const f = emptyField();
    patchAuthor(id, (a) => {
      const at = afterLineId === undefined ? a.lines.length : a.lines.findIndex((l) => l.id === afterLineId) + 1;
      return { ...a, lines: [...a.lines.slice(0, at), f, ...a.lines.slice(at)] };
    });
    setFocusId(f.id);
  };
  const addNote = (id: number) => {
    const f = emptyField();
    patchAuthor(id, (a) => ({ ...a, notes: [...a.notes, f] }));
    setFocusId(f.id);
  };

  const authorKeys = (index: number) => (e: KeyboardEvent<HTMLDivElement>) => {
    if (!e.altKey || !multi || (e.key !== "ArrowUp" && e.key !== "ArrowDown")) return;
    e.preventDefault();
    const fieldId = (e.target as HTMLElement).getAttribute("data-field-id");
    moveAuthor(index, e.key === "ArrowUp" ? -1 : 1, fieldId);
  };

  const removeButton = (label: string, onClick: () => void) => (
    <button type="button" className={styles.iconButton} aria-label={label} data-testid={T.remove} onClick={onClick}>
      ✕
    </button>
  );

  /** ↑ ↓ ✕ on a card; on a phone-width sheet they live behind ⋯. */
  const cardControls = (a: DAuthor, index: number) => {
    const label = a.name.text || `author ${index + 1}`;
    const buttons = (
      <>
        <button
          type="button"
          className={styles.iconButton}
          data-testid={T.moveUp}
          aria-label={`Move ${label} up`}
          disabled={index === 0}
          onClick={() => moveAuthor(index, -1)}
        >
          ↑
        </button>
        <button
          type="button"
          className={styles.iconButton}
          data-testid={T.moveDown}
          aria-label={`Move ${label} down`}
          disabled={index === authors.length - 1}
          onClick={() => moveAuthor(index, 1)}
        >
          ↓
        </button>
        {removeButton(`Remove ${label}`, () => removeAuthor(a.id))}
      </>
    );
    if (!sheet) return buttons;
    return (
      <>
        <button
          type="button"
          className={styles.iconButton}
          data-testid={T.more}
          aria-label={`More for ${label}`}
          aria-expanded={menuFor === a.id}
          onClick={() => setMenuFor(menuFor === a.id ? null : a.id)}
        >
          ⋯
        </button>
        {menuFor === a.id && <div className={styles.menu}>{buttons}</div>}
      </>
    );
  };

  const renderCollapsed = (a: DAuthor, index: number) => {
    return (
      <div key={a.id} className={`${styles.card} ${styles.collapsed}`} data-testid={T.author} data-index={index} data-collapsed="true">
        <div className={styles.cardHead}>
          <span className={styles.grip} aria-hidden="true">
            ⠿
          </span>
          <span className={styles.number}>{index + 1}</span>
          <button
            type="button"
            className={styles.summary}
            data-testid={T.toggle}
            aria-expanded="false"
            onClick={() => {
              setOpenId(a.id);
              setFocusId(a.name.id);
            }}
          >
            <strong>{a.name.text || <em>{i18n.t("editor:titleEdit.unnamed")}</em>}</strong>
            {a.lines[0] && <span className={styles.summaryLine}>{a.lines[0].text}</span>}
            {symbols.authors[index].notes.length > 0 && (
              <span className={styles.summarySymbols}>{symbols.authors[index].notes.join(" ")}</span>
            )}
          </button>
          {cardControls(a, index)}
        </div>
      </div>
    );
  };

  const renderAuthor = (a: DAuthor, index: number) =>
    authors.length >= collapseAt && openId !== a.id ? renderCollapsed(a, index) : renderOpenAuthor(a, index);

  const renderOpenAuthor = (a: DAuthor, index: number) => (
    <div
      key={a.id}
      className={multi ? styles.card : styles.plain}
      data-testid={T.author}
      data-index={index}
      onKeyDown={authorKeys(index)}
    >
      <div className={styles.cardHead}>
        {multi && (
          <>
            <span className={styles.grip} aria-hidden="true">
              ⠿
            </span>
            <span className={styles.number}>{index + 1}</span>
          </>
        )}
        <FieldInput
          field={a.name}
          testId={T.authorName}
          ariaLabel={`Author ${index + 1} name`}
          placeholder="Ada Lovelace"
          strong={multi}
          autoFocus={focus?.author === index}
          onChange={(f) => patchAuthor(a.id, (x) => ({ ...x, name: f }))}
          onKeyDown={fieldKeys("name")}
        />
        {multi && (
          cardControls(a, index)
        )}
      </div>
      {a.lines.map((line, li) => (
        <div key={line.id} className={`${styles.row} ${multi ? styles.indent : ""}`}>
          <FieldInput
            field={line}
            testId={T.authorLine}
            ariaLabel={`Author ${index + 1} line ${li + 1}`}
            placeholder={i18n.t("editor:titleEdit.affiliationDepartmentEMail")}
            onChange={(f) => patchAuthor(a.id, (x) => ({ ...x, lines: x.lines.map((l) => (l.id === f.id ? f : l)) }))}
            onKeyDown={fieldKeys(
              "line",
              () => patchAuthor(a.id, (x) => ({ ...x, lines: x.lines.filter((l) => l.id !== line.id) })),
              () => addLine(a.id, line.id),
            )}
          >
            {removeButton("Remove line", () =>
              patchAuthor(a.id, (x) => ({ ...x, lines: x.lines.filter((l) => l.id !== line.id) })),
            )}
          </FieldInput>
        </div>
      ))}
      <div className={`${styles.row} ${multi ? styles.indent : ""}`}>
        <FieldInput
          field={a.email}
          testId={T.authorEmail}
          ariaLabel={`Author ${index + 1} email`}
          placeholder={i18n.t("editor:titleEdit.email")}
          onChange={(f) => patchAuthor(a.id, (x) => ({ ...x, email: f }))}
          onKeyDown={fieldKeys("name")}
        />
      </div>
      {a.notes.map((note, ni) => (
        <div
          key={note.id}
          className={`${styles.row} ${multi ? styles.indent : ""}`}
          onMouseEnter={() => setHoverSymbol(symbols.authors[index].notes[ni])}
          onMouseLeave={() => setHoverSymbol(null)}
        >
          <span className={styles.badge} data-testid={T.noteSymbol}>
            {symbols.authors[index].notes[ni]}
          </span>
          <FieldInput
            field={note}
            testId={T.authorNote}
            ariaLabel={`Author ${index + 1} note ${ni + 1}`}
            placeholder="Note (funding, e-mail, equal contribution)"
            onChange={(f) => patchAuthor(a.id, (x) => ({ ...x, notes: x.notes.map((n) => (n.id === f.id ? f : n)) }))}
            onKeyDown={fieldKeys("note", () =>
              patchAuthor(a.id, (x) => ({ ...x, notes: x.notes.filter((n) => n.id !== note.id) })),
            )}
          >
            {removeButton("Remove note", () =>
              patchAuthor(a.id, (x) => ({ ...x, notes: x.notes.filter((n) => n.id !== note.id) })),
            )}
          </FieldInput>
        </div>
      ))}
      <div className={`${styles.links} ${multi ? styles.indent : ""}`}>
        <button type="button" className={styles.quiet} data-testid={T.addLine} onClick={() => addLine(a.id)}>
          {i18n.t("editor:titleEdit.affiliation")}
        </button>
        <button type="button" className={styles.quiet} data-testid={T.addNote} onClick={() => addNote(a.id)}>
          {i18n.t("editor:titleEdit.note")}
        </button>
      </div>
    </div>
  );

  return (
    <div
      ref={rootRef}
      className={`${styles.root} ${layout === "mid" ? styles.rootMid : ""} ${sheet ? styles.rootSheet : ""}`}
      data-layout={layout}
      data-testid={T.modal}
      data-hover-symbol={hoverSymbol ?? undefined}
      onKeyDown={(e) => {
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          onSubmit();
        }
      }}
    >
      <div className={styles.form}>
        <div>
          <label className={styles.label} htmlFor="title-edit-title">
            {i18n.t("editor:titleEdit.title")}
          </label>
          <div className={styles.row}>
            <input
              id="title-edit-title"
              data-testid="title-modal-title"
              data-field-id="title"
              className={styles.titleInput}
              type="text"
              value={titleText}
              placeholder={i18n.t("editor:titleEdit.paperTitle")}
              autoFocus={focus?.author === undefined}
              onChange={(e) => {
                setTitleTouched(true);
                setTitleText(e.target.value);
              }}
              onKeyDown={(e) => {
                if (isPlainEnter(e)) {
                  e.preventDefault();
                  neighbour(e.currentTarget, 1)?.focus();
                }
              }}
            />
          </div>
          {titleNotes.map((note, ni) => (
            <div
              key={note.id}
              className={styles.row}
              style={{ marginTop: 6 }}
              onMouseEnter={() => setHoverSymbol(symbols.title[ni])}
              onMouseLeave={() => setHoverSymbol(null)}
            >
              <span className={styles.badge} data-testid={T.noteSymbol}>
                {symbols.title[ni]}
              </span>
              <FieldInput
                field={note}
                testId={T.titleNote}
                ariaLabel={`Title note ${ni + 1}`}
                placeholder={i18n.t("editor:titleEdit.noteOnTheTitle")}
                onChange={(f) => editTitleNotes((l) => l.map((n) => (n.id === f.id ? f : n)))}
                onKeyDown={fieldKeys("note", () => editTitleNotes((l) => l.filter((n) => n.id !== note.id)))}
              >
                {removeButton("Remove title note", () => editTitleNotes((l) => l.filter((n) => n.id !== note.id)))}
              </FieldInput>
            </div>
          ))}
          <button
            type="button"
            className={styles.quiet}
            data-testid={T.addTitleNote}
            onClick={() => {
              const f = emptyField();
              editTitleNotes((l) => [...l, f]);
              setFocusId(f.id);
            }}
          >
            {i18n.t("editor:titleEdit.note")}
          </button>
        </div>

        <div>
          <div className={styles.authorsHeader}>
            <span className={styles.label}>{multi ? "Authors" : "Author"}</span>
            <ModeSwitch mode={mode} status={reading.status} onChange={switchMode} />
          </div>
          {mode === "latex" ? (
            <LatexBox value={latexDraft} reading={reading} onChange={editLatex} />
          ) : (
            <>
              {authors.map(renderAuthor)}
              {duplicates.map((d) => (
                <p key={d.text} className={styles.duplicate} data-testid={T.duplicateNote}>
                  {duplicateMessage(d)}
                </p>
              ))}
              <button type="button" className={styles.tonal} data-testid={T.addAuthor} onClick={addAuthor}>
                {i18n.t("editor:titleEdit.addAuthor")}
              </button>
            </>
          )}
        </div>

        <div>{dateSlot}</div>
      </div>

      {sheet ? (
        <PreviewStrip
          title={titleValue}
          author={authorValue}
          status={reading.status}
          dateText={dateText}
          highlightSymbol={hoverSymbol}
          width={Math.min(windowWidth - 32, 595)}
        />
      ) : (
        <div className={styles.paperColumn}>
          <p className={styles.paperCaption}>As it prints · A4 · article</p>
          <PaperPreview
            title={titleValue}
            author={authorValue}
            status={reading.status}
            dateText={dateText}
            highlightSymbol={hoverSymbol}
            width={layout === "mid" ? 300 : 380}
          />
        </div>
      )}
    </div>
  );
}
